import Box from '@/common/components/Box'
import ResumeItem from '@/common/components/ResumeItem'
import { cn } from '@/common/utils/cn'
import { formatDateTime } from '@/common/utils/formatters'
import TransportAutoEntriesDeliveryCode from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/CheckinEntry/Transport/AutoEntries/DeliveryCode'
import { checkinEntryOptions } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/directiveEntryFormSteps/checkinEntry/checkinEntryOptions.constant'
import {
  MaintenanceForwardingSectionStyle,
  maintenanceForwardingSectionStyles,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingSectionStyles.constant'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { useMemo } from 'react'

interface MaintenanceWorkflowForwardingCheckinEntrySectionProps {
  showTransportAutoEntriesDeliveryCode?: boolean
}

const MaintenanceWorkflowForwardingCheckinEntrySection = ({
  showTransportAutoEntriesDeliveryCode,
}: MaintenanceWorkflowForwardingCheckinEntrySectionProps) => {
  const { forwarding, noActions } = useForwardingTabContext();

  const sectionStyle = useMemo(() => {
    if (forwarding?.checkin_entry?.completed_at) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Completed];
    }

    if (showTransportAutoEntriesDeliveryCode && !noActions) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Pending];
    }

    return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Waiting];
  }, [forwarding?.checkin_entry?.completed_at, showTransportAutoEntriesDeliveryCode, noActions]);

  return (
    <Box className={cn('p-4 flex flex-col gap-4', sectionStyle)}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">Método de check-in</p>

          {forwarding?.checkin_entry?.completed_at && (
            <div className="flex items-center gap-1 -mb-1">
              <p className="text-valid text-xs">
                Check-in concluído
              </p>
            </div>
          )}
        </div>

        {showTransportAutoEntriesDeliveryCode && !noActions && <TransportAutoEntriesDeliveryCode />}
      </div>

      <div className="flex gap-4">
        {forwarding?.checkin_entry?.photos && forwarding?.checkin_entry?.photos?.length > 0 && (
          <ImageCarousel
            imageClassName='object-contain'
            className='size-52'
            files={forwarding?.checkin_entry?.photos?.map((file) => ({ id: file.id, file: file.url }))}
            showRemoveButton={false}
            showExpandButton
          />
        )}

        <div className="flex flex-col gap-3">
          <div className="flex gap-4">
            {forwarding?.checkin_entry?.completed_at && (
              <ResumeItem
                label="Check-in realizado"
                value={formatDateTime(new Date(forwarding?.checkin_entry?.completed_at))}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}

            {forwarding?.checkin_entry?.completed_at && forwarding?.checkin_entry?.completed_by && (
              <ResumeItem
                label="Usuário que realizou o check-in"
                value={forwarding.checkin_entry.completed_by.full_name}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>

          <div className="flex gap-4">
            {forwarding?.checkin_entry_type && (
              <ResumeItem
                label="Tipo de Transporte"
                value={checkinEntryOptions[forwarding.checkin_entry_type].title || '--'}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}

            {forwarding?.checkin_entry?.responsible && (
              <ResumeItem
                label="Responsável por Transporte"
                value={forwarding?.checkin_entry?.responsible.full_name || '--'}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>

          {forwarding?.checkin_entry?.observations && (
            <ResumeItem
              label='Observações de check-in'
              value={forwarding?.checkin_entry.observations || '--'}
              valueClassName='text-sm'
            />
          )}
        </div>
      </div>
    </Box>
  )
}

export default MaintenanceWorkflowForwardingCheckinEntrySection
